import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import PageTitle from '../layouts/PageTitle';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { axiosPut } from '../../utils/axiosPut';
import { getNominationRoundYear } from '../../utils/getNominationRoundYear';
import { useAllocationWarnings } from '../hooks/useAllocationWarnings';
import {
  groupNominationsByCommittee,
  NA_VALUE,
} from './CouncilMemberAllocation/allocationHelpers';
import CommitteeAllocationCard from './CouncilMemberAllocation/CommitteeAllocationCard';
import ExportAllocationsButton from './CouncilMemberAllocation/ExportAllocationsButton';
import { ELECTION_TYPE, NOMINATION_STATUS } from '../../utils/constants';
import { devError } from '../../utils/devLog';

const CouncilMemberAllocation = () => {
  const { sectionalCommitteesWithCmse, activeFellowsArray } =
    useStaticCollections();

  const {
    nominationArray,
    councilMembersArray,
    candidateArray,
    nominationStateArray,
    coiDeclarationArray,
    isLoading,
    triggerNominationStateRefresh,
  } = useAdminCollections();

  const [allocations, setAllocations] = useState({});
  const [dirtyCommittees, setDirtyCommittees] = useState(new Set());
  const [notification, setNotification] = useState({
    message: '',
    title: '',
    icon: null,
    iconColor: null,
  });
  const [savingCommittee, setSavingCommittee] = useState(null);
  const [rankedNominations, setRankedNominations] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(true);

  const clearNotification = () => {
    setNotification({
      message: '',
      title: '',
      icon: null,
      iconColor: null,
    });
  };

  const showNotification = (
    message,
    title,
    icon = null,
    iconColor = null,
    duration = 10000,
  ) => {
    setNotification({ message, title, icon, iconColor });
    setTimeout(clearNotification, duration);
  };

  useEffect(() => {
    const fetchAllRankings = async () => {
      if (
        isLoading ||
        nominationArray.length === 0 ||
        nominationStateArray.length === 0
      )
        return;

      try {
        setLoadingRankings(true);

        const eligibleNominations = nominationArray.filter((nom) => {
          const statusTypeId =
            nom.attributes?.nominationStatusTypeId ||
            nom.nominationStatusTypeId;
          return statusTypeId === NOMINATION_STATUS.RECOMMENDED;
        });

        const nominationsWithRankings = eligibleNominations
          .map((nom) => {
            const nominationState = nominationStateArray.find(
              (ns) =>
                (ns.attributes?.nominationId || ns.nominationId) === nom.id,
            );

            if (!nominationState) return null;

            const ranking =
              nominationState.attributes?.ranking || nominationState.ranking;
            const councilMemberUserId =
              nominationState.attributes?.councilMemberUserId ||
              nominationState.councilMemberUserId;

            if (ranking && ranking >= 1) {
              return {
                ...nom,
                ranking,
                nominationStateId: nominationState.id,
                councilMemberUserId,
              };
            }
            return null;
          })
          .filter(Boolean);

        setRankedNominations(nominationsWithRankings);

        const initialAllocations = {};

        nominationsWithRankings.forEach((nomination) => {
          const nomElectionTypeId = nomination.attributes?.electionTypeId;

          if (
            nomination.councilMemberUserId === NA_VALUE &&
            nomElectionTypeId === 2
          ) {
            initialAllocations[nomination.id] = {
              value: NA_VALUE,
              label: 'N/A',
            };
          } else if (
            nomination.councilMemberUserId &&
            nomination.councilMemberUserId !== NA_VALUE
          ) {
            const councilMember = councilMembersArray.find((cm) => {
              const user = cm.attributes?.users_permissions_user?.data;
              return user?.id === nomination.councilMemberUserId;
            });

            if (councilMember) {
              const user =
                councilMember.attributes?.users_permissions_user?.data;

              if (user) {
                const fellowId = user.attributes?.fellowId;

                const fellow = activeFellowsArray.find(
                  (f) => f.id === parseInt(fellowId),
                );

                if (fellow) {
                  initialAllocations[nomination.id] = {
                    value: user.id,
                    label: fellow.attributes.label,
                  };
                }
              }
            }
          } else {
            initialAllocations[nomination.id] = null;
          }
        });

        setAllocations(initialAllocations);
      } catch (error) {
        devError('Error fetching rankings:', error);
        showNotification(
          'Failed to load rankings',
          'Error',
          'bi-exclamation-triangle-fill',
          'text-danger',
          5000,
        );
      } finally {
        setLoadingRankings(false);
      }
    };

    fetchAllRankings();
  }, [
    nominationArray,
    nominationStateArray,
    councilMembersArray,
    activeFellowsArray,
    isLoading,
  ]);

  const getCouncilMemberOptions = (
    ranking,
    candidateId,
    nominationId,
    electionTypeId,
  ) => {
    const currentRound = getNominationRoundYear(new Date());

    const options = councilMembersArray
      .map((councilMember) => {
        const user = councilMember.attributes?.users_permissions_user?.data;
        if (user) {
          const fellow = activeFellowsArray.find(
            (f) => f.id === parseInt(user.attributes?.fellowId),
          );
          if (fellow) {
            // Check if this council member has direct COI with this candidate
            const hasDirectCOI = coiDeclarationArray?.some((declaration) => {
              const declaredUserId =
                declaration.attributes?.userId || declaration.userId;
              const declaredNominationId =
                declaration.attributes?.nominationId ||
                declaration.nominationId;
              const declaredCandidateId =
                declaration.attributes?.candidateId || declaration.candidateId;
              const nominationRound =
                declaration.attributes?.nominationRound ||
                declaration.nominationRound;

              const coiTypes = declaration.attributes?.coiTypes?.data || [];
              const hasDirectCategory = coiTypes.some(
                (type) => type.attributes?.category === 'direct',
              );

              return (
                declaredUserId === user.id &&
                declaredNominationId === nominationId &&
                declaredCandidateId === candidateId &&
                nominationRound === currentRound &&
                hasDirectCategory
              );
            });

            return {
              value: user.id,
              label: hasDirectCOI
                ? `${fellow.attributes.label} (Direct conflict)`
                : fellow.attributes.label,
              isDisabled: hasDirectCOI,
            };
          }
        }
        return null;
      })
      .filter(Boolean);

    // Only include N/A option for ranking 1 and ordinary elections
    if (ranking === 1 && electionTypeId === ELECTION_TYPE.ORDINARY) {
      return [{ value: NA_VALUE, label: 'N/A' }, ...options];
    }

    return options;
  };

  const groupedData = useMemo(
    () =>
      groupNominationsByCommittee(
        rankedNominations,
        sectionalCommitteesWithCmse,
        candidateArray,
      ),
    [rankedNominations, sectionalCommitteesWithCmse, candidateArray],
  );

  const allocationWarnings = useAllocationWarnings(allocations, groupedData);

  useEffect(() => {
    if (loadingRankings || !allocationWarnings) return;

    const directCOIWarnings = [];

    Object.entries(allocationWarnings).forEach(([nominationId, warnings]) => {
      const directCOI = warnings.find((w) => w.type === 'direct-coi');
      if (directCOI) {
        const nomination = rankedNominations.find(
          (n) => n.id === parseInt(nominationId),
        );
        if (nomination) {
          const candidate = candidateArray.find(
            (c) => c.id === nomination.attributes.candidateId,
          );
          if (candidate) {
            directCOIWarnings.push({
              candidateName: `${candidate.attributes.name} ${candidate.attributes.lastName}`,
              councilMemberName: directCOI.councilMemberName,
            });
          }
        }
      }
    });

    if (directCOIWarnings.length > 0) {
      let message = '<div style="line-height: 1.6;">';
      message +=
        '<p>The following allocations have direct COI declarations:</p>';
      directCOIWarnings.forEach((warning) => {
        message += `• <strong>${warning.councilMemberName}</strong> → ${warning.candidateName}<br/>`;
      });
      message += '</div>';

      showNotification(
        message,
        'Direct COI warning',
        'bi-exclamation-circle-fill',
        'text-danger',
        30000,
      );
    }
  }, [allocationWarnings, loadingRankings, rankedNominations, candidateArray]);

  const handleAllocationChange = (
    nominationId,
    selectedOption,
    committeeId,
  ) => {
    setAllocations((prev) => ({
      ...prev,
      [nominationId]: selectedOption,
    }));
    setDirtyCommittees((prev) => new Set(prev).add(committeeId));
  };

  const handleSaveCommittee = async (committeeId, committeeName) => {
    setSavingCommittee(committeeId);

    try {
      const committee = groupedData.find((c) => c.committeeId === committeeId);

      if (!committee) {
        throw new Error('Committee not found');
      }

      const savePromises = committee.nominations.map(async (nomination) => {
        const allocation = allocations[nomination.nominationId];
        const councilMemberUserId =
          allocation?.value === NA_VALUE
            ? NA_VALUE
            : (allocation?.value ?? null);

        if (!nomination.nominationStateId) {
          devError(
            `No nomination_state ID found for nomination ${nomination.nominationId}`,
          );
          return;
        }

        const payload = {
          data: {
            councilMemberUserId,
          },
        };

        await axiosPut(
          `nomination-states/${nomination.nominationStateId}`,
          payload,
        );

        await triggerNominationStateRefresh(nomination.nominationId);
      });

      await Promise.all(savePromises);

      showNotification(
        `Allocations saved for ${committeeName}`,
        'Saved successfully',
      );
      setDirtyCommittees((prev) => {
        const newSet = new Set(prev);
        newSet.delete(committeeId);
        return newSet;
      });
    } catch (error) {
      devError('Error saving allocations:', error);
      showNotification(
        'Failed to save allocations',
        'Error',
        'bi-exclamation-triangle-fill',
        'text-danger',
        5000,
      );
    } finally {
      setSavingCommittee(null);
    }
  };

  const baseSelectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      borderRadius: '0.5rem',
      borderColor: '#B3B3B3',
      minHeight: '38px',
    }),
  };

  const getUnsavedChangesMessage = () => {
    if (dirtyCommittees.size === 0) return null;

    const dirtyCommitteeNames = groupedData
      .filter((committee) => dirtyCommittees.has(committee.committeeId))
      .map((committee) => committee.committeeName);

    if (dirtyCommitteeNames.length === 1) {
      return `You have unsaved changes in ${dirtyCommitteeNames[0]}. Please save your allocations.`;
    }
    if (dirtyCommitteeNames.length === 2) {
      return `You have unsaved changes in ${dirtyCommitteeNames[0]} and ${dirtyCommitteeNames[1]}. Please save your allocations.`;
    }
    const lastCommittee = dirtyCommitteeNames.pop();
    return `You have unsaved changes in ${dirtyCommitteeNames.join(', ')}, and ${lastCommittee}. Please save your allocations.`;
  };

  if (isLoading || loadingRankings) {
    return (
      <>
        <PageTitle
          motherMenu="Administration"
          activeMenu="Council Member Allocation"
        />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '400px' }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        motherMenu="Administration"
        activeMenu="Council Member Allocation"
        notificationMessage={getUnsavedChangesMessage() || notification.message}
        notificationTitle={
          dirtyCommittees.size > 0 ? 'Unsaved Changes' : notification.title
        }
        notificationIcon={notification.icon}
        notificationIconColor={notification.iconColor}
      />

      <ExportAllocationsButton
        groupedData={groupedData}
        allocations={allocations}
        rankedNominations={rankedNominations}
      />

      {groupedData.length === 0 ? (
        <Alert variant="info">
          No ranked candidates found. To appear here, candidates must be both
          Recommended to Council and have a ranking assigned.
        </Alert>
      ) : (
        groupedData.map((committee) => (
          <CommitteeAllocationCard
            key={committee.committeeId}
            committee={committee}
            allocations={allocations}
            getCouncilMemberOptions={getCouncilMemberOptions}
            allocationWarnings={allocationWarnings}
            onAllocationChange={(nominationId, selectedOption) =>
              handleAllocationChange(
                nominationId,
                selectedOption,
                committee.committeeId,
              )
            }
            onSave={handleSaveCommittee}
            isSaving={savingCommittee === committee.committeeId}
            selectStyles={baseSelectStyles}
          />
        ))
      )}
    </>
  );
};

export default CouncilMemberAllocation;
